'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Film, Tv, Play, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import AppImage from '@/components/ui/AppImage';

interface SearchResult {
  id: string;
  title: string;
  type: 'movie' | 'series' | 'episode';
  slug?: string;
  poster_url?: string;
  year?: number;
  genre?: string;
  series_title?: string;
  series_slug?: string;
}

interface GlobalSearchProps {
  onClose?: () => void;
}

export default function GlobalSearch({ onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const supabase = createClient();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const search = useCallback(async (q: string) => {
    if (!q.trim() || q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const term = `%${q.trim()}%`;
      const [moviesRes, seriesRes, episodesRes] = await Promise.all([
        supabase
          .from('movies')
          .select('id, title, slug, poster_url, year, genre')
          .ilike('title', term)
          .limit(4),
        supabase
          .from('series')
          .select('id, title, slug, poster_url, year, genre')
          .ilike('title', term)
          .limit(4),
        supabase
          .from('episodes')
          .select('id, title, series_id, series:series_id(title, slug)')
          .ilike('title', term)
          .limit(3),
      ]);

      const movies: SearchResult[] = (moviesRes.data || []).map((m) => ({
        id: m.id,
        title: m.title,
        type: 'movie' as const,
        slug: m.slug,
        poster_url: m.poster_url,
        year: m.year,
        genre: m.genre,
      }));

      const series: SearchResult[] = (seriesRes.data || []).map((s) => ({
        id: s.id,
        title: s.title,
        type: 'series' as const,
        slug: s.slug,
        poster_url: s.poster_url,
        year: s.year,
        genre: s.genre,
      }));

      const episodes: SearchResult[] = (episodesRes.data || []).map((e: any) => ({
        id: e.id,
        title: e.title,
        type: 'episode' as const,
        series_title: e.series?.title,
        series_slug: e.series?.slug,
      }));

      setResults([...movies, ...series, ...episodes]);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getHref = (r: SearchResult) => {
    if (r.type === 'movie') return `/movie-detail-page?movie=${r.slug}`;
    if (r.type === 'series') return `/series/${r.slug}`;
    if (r.type === 'episode' && r.series_slug) return `/series/${r.series_slug}`;
    return '#';
  };

  const TypeIcon = ({ type }: { type: SearchResult['type'] }) => {
    if (type === 'movie') return <Film size={12} className="text-primary" />;
    if (type === 'series') return <Tv size={12} className="text-accent" />;
    return <Play size={12} className="text-neon-pink" />;
  };

  const typeLabel = (type: SearchResult['type']) => {
    if (type === 'movie') return 'Movie';
    if (type === 'series') return 'Series';
    return 'Episode';
  };

  const showDropdown = focused && (query.length >= 2);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search movies, series, episodes..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          className="w-full neon-input rounded-xl pl-9 pr-10 py-2.5 text-sm"
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          {loading && <Loader2 size={14} className="animate-spin text-primary" />}
          {query && (
            <button
              onClick={() => { setQuery(''); setResults([]); inputRef.current?.focus(); }}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 glass-card border border-border rounded-xl overflow-hidden z-[100] shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
          {results.length === 0 && !loading && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No results for &ldquo;{query}&rdquo;
            </div>
          )}
          {results.length > 0 && (
            <div className="max-h-80 overflow-y-auto">
              {results.map((r) => (
                <Link
                  key={`search-${r.type}-${r.id}`}
                  href={getHref(r)}
                  onClick={() => { setFocused(false); setQuery(''); onClose?.(); }}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-border/50 last:border-0 group"
                >
                  {/* Poster */}
                  <div className="w-9 h-12 rounded-md overflow-hidden flex-shrink-0 bg-muted">
                    {r.poster_url ? (
                      <AppImage
                        src={r.poster_url}
                        alt={r.title}
                        width={36}
                        height={48}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <TypeIcon type={r.type} />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                      {r.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                        <TypeIcon type={r.type} />
                        {typeLabel(r.type)}
                      </span>
                      {r.year && <span className="text-[10px] text-muted-foreground">{r.year}</span>}
                      {r.genre && <span className="text-[10px] text-muted-foreground truncate">{r.genre}</span>}
                      {r.type === 'episode' && r.series_title && (
                        <span className="text-[10px] text-muted-foreground truncate">from {r.series_title}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
